import { environment } from '../../config/environment';
import { getAccessToken } from '../authService';
import { getBrowserSessionId, getTabInstanceId } from './identity';

const BATCH_ENDPOINT = '/api/v1/analytics/track/batch';
/** Must stay under the endpoint's hard limit of 100 events per request. */
const MAX_BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 5000;
/** Guards against unbounded growth if the backend is unreachable for a long time. */
const MAX_QUEUE_SIZE = 200;

export interface QueuedEvent {
  event: string;
  occurred_at: string;
  properties?: Record<string, unknown>;
}

interface BatchContext {
  browser_session_id: string;
  tab_instance_id: string;
  app_version: string;
  route: string;
  device_type: 'mobile' | 'tablet' | 'desktop';
  timezone: string;
}

interface BatchPayload {
  sent_at: string;
  context: BatchContext;
  events: QueuedEvent[];
}

interface RejectedEvent {
  index: number;
  reason: string;
}

interface BatchResponse {
  accepted: number;
  duplicates: number;
  failed: number;
  rejected: RejectedEvent[];
  received_at: string;
}

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let cachedToken: string | null = null;

const getDeviceType = (): BatchContext['device_type'] => {
  const width = typeof window === 'undefined' ? 0 : window.innerWidth;
  if (width > 0 && width < 640) return 'mobile';
  if (width >= 640 && width < 1024) return 'tablet';
  return 'desktop';
};

// userId is intentionally omitted — the backend derives it from the bearer token.
const buildContext = (): BatchContext => ({
  browser_session_id: getBrowserSessionId(),
  tab_instance_id: getTabInstanceId(),
  app_version: environment.VITE_APP_VERSION,
  route: typeof window === 'undefined' ? '' : `${window.location.pathname}${window.location.search}`,
  device_type: getDeviceType(),
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

const buildUrl = (): string => `${environment.VITE_APP_API_BASE_URL}${BATCH_ENDPOINT}`;

const clearTimer = (): void => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
};

const scheduleFlush = (): void => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
};

/**
 * The batch endpoint answers 202 even when every event was rejected, so the response
 * body is the only signal. `duplicates` is the expected result of a keepalive retry
 * and is deliberately not reported.
 */
const reportBatchOutcome = (result: BatchResponse, events: QueuedEvent[]): void => {
  if (result.rejected?.length) {
    // Client-side contract violation — retrying would not help.
    console.warn(
      'Analytics events rejected by the backend:',
      result.rejected.map(({ index, reason }) => ({ event: events[index]?.event, index, reason })),
    );
  }

  if (result.failed > 0) {
    console.warn(`Analytics backend accepted but could not store ${result.failed} event(s).`);
  }
};

const sendBatch = async (payload: BatchPayload): Promise<void> => {
  const token = await getAccessToken();
  if (token) cachedToken = token;

  const response = await fetch(buildUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    // A malformed envelope returns FastAPI's own 422 shape, not BatchResponse.
    const detail = await response.text().catch(() => '');
    throw new Error(
      `Analytics batch failed with status ${response.status}${detail ? `: ${detail.slice(0, 500)}` : ''}`,
    );
  }

  let result: BatchResponse | null = null;
  try {
    result = (await response.json()) as BatchResponse;
  } catch {
    return;
  }

  if (result) reportBatchOutcome(result, payload.events);
};

/** Flushes the queue over the network. Failures are swallowed; events are dropped, not retried. */
export const flush = async (): Promise<void> => {
  clearTimer();
  if (queue.length === 0) return;

  const events = queue.splice(0, MAX_BATCH_SIZE);

  try {
    await sendBatch({ sent_at: new Date().toISOString(), context: buildContext(), events });
  } catch (error) {
    console.warn('Failed to flush analytics batch:', error);
  }

  if (queue.length > 0) scheduleFlush();
};

/**
 * Best-effort flush during page teardown. Uses `keepalive` fetch so the auth header
 * survives; `sendBeacon` cannot set headers so it is only a last resort.
 *
 * Events are removed from the queue before sending and are never re-sent. This matters:
 * only `surface_time_spent` carries a de-duplication key, so a retry of a batch holding
 * count-based events (`task_completed`, `chat_input_submitted`) would double-count them.
 * Any future retry logic must exclude those or give them a stable key first.
 */
export const flushOnUnload = (): void => {
  clearTimer();
  if (queue.length === 0) return;

  const events = queue.splice(0, MAX_BATCH_SIZE);
  const body = JSON.stringify({ sent_at: new Date().toISOString(), context: buildContext(), events });

  try {
    if (cachedToken) {
      void fetch(buildUrl(), {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cachedToken}` },
        body,
      }).catch(() => undefined);
      return;
    }

    navigator.sendBeacon?.(buildUrl(), new Blob([body], { type: 'application/json' }));
  } catch {
    // Nothing more we can do while the page is going away.
  }
};

export const enqueue = (event: QueuedEvent): void => {
  if (queue.length >= MAX_QUEUE_SIZE) {
    queue.shift();
  }
  queue.push(event);

  if (queue.length >= MAX_BATCH_SIZE) {
    void flush();
    return;
  }
  scheduleFlush();
};

let listenersAttached = false;

export const startAnalyticsQueue = (): void => {
  if (listenersAttached || typeof window === 'undefined') return;
  listenersAttached = true;

  window.addEventListener('pagehide', flushOnUnload);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnUnload();
  });
};

/** Test seam. */
export const resetQueueForTests = (): void => {
  clearTimer();
  queue = [];
  cachedToken = null;
};
